import { useEffect } from "react";
import { observer } from "mobx-react";
import { FormGroup, Input, Label } from "reactstrap";
import Select from "react-select";
import Switch from "react-switch";
import styled from "styled-components";
import { automationStore } from "stores";
import { Enum } from "helpers";
import { AutomationType, TriggerType, PrimaryRecordType, UserTaskPriority, UserTaskDateMode, OwnerTypeCandidate, OwnerTypeClient, UpdateRecordOwnerType } from "helpers/enum";
import { RappidNodeType, RappidText } from "helpers/Constants";
import { inputNumberOnlyOnKeypressHandler, inputNumberOnPasteHandler } from "helpers/Validator";
import stepUpdateRecordIcon from "assets/img/automation/step-updaterecord.svg";
import HeaderForm from "./HeaderForm";
import SubmitButton from "./SubmitButton";
import StyledMultiSelectWithSearch from "../../common/StyledMultiSelectWithSearch";
import { validateNodeAndResetBorder } from "components/Rappid/RappidCanvasFunctions";
import { SendFromButtonGroup, SendFromButton, SendFromDropdownContainer } from "components/Automation/Styled";

const getDefaultDateISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  return d.toISOString().slice(0, 10);
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const RECORD_TYPE_LABEL = {
  [PrimaryRecordType.Candidate]: 'Candidate',
  [PrimaryRecordType.Contact]: 'Contact',
  [PrimaryRecordType.Company]: 'Company',
  [PrimaryRecordType.Job]: 'Job',
  [PrimaryRecordType.Placement]: 'Placement',
  [PrimaryRecordType.Application]: 'Application',
};

const PRIORITY_OPTIONS = [
  { value: UserTaskPriority.None, label: 'None' },
  { value: UserTaskPriority.Low, label: 'Low' },
  { value: UserTaskPriority.Medium, label: 'Medium' },
  { value: UserTaskPriority.High, label: 'High' },
];

// ─── Subcomponents ──────────────────────────────────────────────────────────


const FL = ({ children }) => (
  <Label style={{
    fontSize: 12.5,
    color: '#d6d6d6',
    margin: '0 0 5px',
    display: 'block',
    fontWeight: 400,
  }}>
    {children}
  </Label>
);


const Hint = ({ children }) => (
  <p style={{ fontSize: 11.5, color: '#9a9a9c', margin: '4px 0 12px' }}>
    {children}
  </p>
);

// ─── Fixed date ──────────────────────────────────────────────────────────────

const FixedDateContainer = styled.div`
  background: #2D2D2D;
  border-radius: 8px;
  border: 1px solid rgb(59, 58, 58);
  overflow: hidden;
`;

const DateTimeGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $singleColumn }) => $singleColumn ? '1fr' : '1fr 1fr'};
  gap: 8px;
  padding: 12px;

  .automation-date-input {
    width: 100%;
    border-radius: 8px;
    padding: 7px 10px;
    font-size: 13px;
    box-sizing: border-box;
  }
`;

const AllDayRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-top: 1px solid rgb(59, 58, 58);
  background: #272727;
`;

const AllDayToggleLabel = styled.label`
  font-size: 12.5px;
  color: #d6d6d6;
  margin: 0;
  cursor: pointer;
  user-select: none;
`;

// ─── Relative due-date inputs (Months/Weeks/Days + Start/End time) ───────────
// Offset units mirror TriggerTimeInput number-field styling; time-of-day + all-day
const RelativeDateContainer = styled.div`
  background: #2D2D2D;
  border-radius: 8px;
  border: 1px solid rgb(59, 58, 58);
  overflow: hidden;
`;

const RelativeDateGroup = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 12px 12px;
`;

const RelativeDateField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 0;

  input {
    background: #1E1E1E;
    border: 1px solid #404040;
    border-radius: 8px;
    color: #FFFFFF !important;
    text-align: center;
    width: 100%;
    padding: 8px;
    margin-bottom: 8px;
    height: 36px;
    font-size: 14px;

    &:focus {
      border-color: #666;
      box-shadow: none;
      outline: none;
      background: #262626;
      color: #FFFFFF !important;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
      background: #1E1E1E;
    }
  }

  small {
    color: #FFFFFF;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const RELATIVE_DATE_UNITS = ['months', 'weeks', 'days'];
const DEFAULT_START_TIME = '09:00:00';
const DEFAULT_END_TIME = '10:00:00';

const toTimeInputValue = (timeSpan, fallback = '09:00') => {
  if (!timeSpan || typeof timeSpan !== 'string') return fallback;
  const match = timeSpan.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return fallback;
  return `${match[1].padStart(2, '0')}:${match[2]}`;
};

const toTimeSpan = (hhmm) => (hhmm && hhmm.length >= 5 ? `${hhmm.slice(0, 5)}:00` : null);

const addOneHour = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return `${String((h + 1) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const RelativeDateInput = ({ formTask, isDisabled }) => {
  const allDay = !!formTask.allDay;
  const startTime = toTimeInputValue(formTask.startTime, '09:00');
  const endTime = toTimeInputValue(formTask.endTime, '10:00');

  const applyRelativeTime = (nextStart, nextEnd, nextAllDay) => {
    const data = { ...formTask, allDay: nextAllDay };
    if (nextAllDay) {
      data.startTime = null;
      data.endTime = null;
    } else {
      data.startTime = toTimeSpan(nextStart) || DEFAULT_START_TIME;
      data.endTime = toTimeSpan(nextEnd) || DEFAULT_END_TIME;
    }
    automationStore.setFormTask(data);
  };

  return (
    <RelativeDateContainer>
      <RelativeDateGroup>
        {RELATIVE_DATE_UNITS.map(unit => (
          <RelativeDateField key={unit}>
            <Input
              type="number"
              id={`automation-task-relative-${unit}`}
              min={0}
              onKeyPress={inputNumberOnlyOnKeypressHandler}
              onPaste={inputNumberOnPasteHandler}
              value={formTask[unit] || 0}
              onFocus={e => e.target.select()}
              disabled={isDisabled}
              onChange={e => automationStore.setFormTask({ ...formTask, [unit]: parseInt(e.target.value) || 0 })}
            />
            <small>{unit}</small>
          </RelativeDateField>
        ))}
      </RelativeDateGroup>

      {!allDay && (
        <DateTimeGrid style={{ borderTop: '1px solid rgb(59, 58, 58)' }}>
          <div>
            <FL>Start time</FL>
            <input
              type="time"
              id="automation-task-relative-start-time"
              className="automation-date-input"
              value={startTime}
              disabled={isDisabled}
              onChange={e => applyRelativeTime(e.target.value, endTime, false)}
            />
          </div>
          <div>
            <FL>End time</FL>
            <input
              type="time"
              id="automation-task-relative-end-time"
              className="automation-date-input"
              value={endTime}
              disabled={isDisabled}
              onChange={e => applyRelativeTime(startTime, e.target.value, false)}
            />
          </div>
        </DateTimeGrid>
      )}

      <AllDayRow>
        <AllDayToggleLabel>All day</AllDayToggleLabel>
        <Switch
          checked={allDay}
          onChange={checked => applyRelativeTime(startTime, endTime, checked)}
          disabled={isDisabled}
          onColor="#ef4277"
          height={20}
          width={36}
          handleDiameter={14}
          uncheckedIcon={false}
          checkedIcon={false}
        />
      </AllDayRow>
    </RelativeDateContainer>
  );
};

// ─── Helpers shared with the Rappid canvas node rendering ───────────────────

export const setTaskNodeTextLine1 = (formTask) => `Task: ${formTask?.title || ''}`;

const describeRelativeDate = (formTask) => {
  const parts = [];
  if (formTask.months) parts.push(`${formTask.months} ${formTask.months > 1 ? 'months' : 'month'}`);
  if (formTask.weeks) parts.push(`${formTask.weeks} ${formTask.weeks > 1 ? 'weeks' : 'week'}`);
  if (formTask.days) parts.push(`${formTask.days} ${formTask.days > 1 ? 'days' : 'day'}`);
  if (parts.length === 0) return '';
  const offset = `Due: ${parts.join(', ')} after created`;
  if (formTask.allDay) return `${offset} (all day)`;
  const start = toTimeInputValue(formTask.startTime, '');
  const end = toTimeInputValue(formTask.endTime, '');
  if (start && end) return `${offset}, ${start}–${end}`;
  if (start) return `${offset}, ${start}`;
  return offset;
};

export const setTaskNodeTextLine2 = (formTask) => {
  if (formTask?.dateMode === UserTaskDateMode.Relative) return describeRelativeDate(formTask || {});
  if (!formTask?.startAt) return '';
  const date = new Date(formTask.startAt);
  if (isNaN(date.getTime())) return '';
  if (formTask.allDay) return `Due: ${date.toISOString().slice(0, 10)}`;
  const startTime = date.toISOString().slice(11, 16);
  const endDate = formTask.endAt ? new Date(formTask.endAt) : null;
  const endTime = endDate && !isNaN(endDate.getTime()) ? endDate.toISOString().slice(11, 16) : null;
  return `Due: ${date.toISOString().slice(0, 10)} ${endTime ? `${startTime}–${endTime}` : startTime}`;
};


const buildAvailableRecords = (isClient, isJobBased, isPlacementBased) => {
  if (isClient) {
    if (isPlacementBased) return [PrimaryRecordType.Contact, PrimaryRecordType.Placement, PrimaryRecordType.Job, PrimaryRecordType.Company];
    if (isJobBased) return [PrimaryRecordType.Contact, PrimaryRecordType.Job, PrimaryRecordType.Company];
    return [PrimaryRecordType.Contact, PrimaryRecordType.Company];
  }
  if (isPlacementBased) return [PrimaryRecordType.Candidate, PrimaryRecordType.Placement, PrimaryRecordType.Job];
  if (isJobBased) return [PrimaryRecordType.Candidate, PrimaryRecordType.Job];
  return [PrimaryRecordType.Candidate];
};

// ─── Main component ──────────────────────────────────────────────────────────

const TaskStepForm = observer(() => {
  const { formTask, automationType, triggerType, automationState, automationUsers } = automationStore;
  const isDraft = automationState === Enum.AutomationStateType.Draft;

  const isClient                  = automationType === AutomationType.Client;
  const isJobBased                = triggerType === TriggerType.JobBased;
  const isPlacementBased          = triggerType === TriggerType.PlacementBased;
  const isPlacementOrJobBased     = isJobBased || isPlacementBased;
  const availableRecords          = buildAvailableRecords(isClient, isJobBased, isPlacementBased);

  const ownerTypeOptions = automationType === AutomationType.Candidate ? OwnerTypeCandidate : OwnerTypeClient;

  const ownerDefaultValue = isPlacementBased
    ? ownerTypeOptions.find(o => o.value === UpdateRecordOwnerType.PlacementRecruiter) ?? ownerTypeOptions[0]
    : isJobBased
      ? ownerTypeOptions.find(o => o.value === UpdateRecordOwnerType.JobOwner) ?? ownerTypeOptions[0]
      : ownerTypeOptions[0];

  const getFilteredOwnerOptions = () => {
    const excluded = new Set([UpdateRecordOwnerType.AdminUser, UpdateRecordOwnerType.NoUser]);
    if (!isPlacementBased) excluded.add(UpdateRecordOwnerType.PlacementRecruiter);
    if (!isJobBased && !isPlacementBased) excluded.add(UpdateRecordOwnerType.JobOwner);
    return ownerTypeOptions.filter(o => !excluded.has(o.value));
  };

  useEffect(() => {
    if (!formTask.dateMode) {
      automationStore.setFormTask({
        title: '',
        dateMode: UserTaskDateMode.Fixed,
        startAt: `${getDefaultDateISO()}T09:00:00.000Z`,
        endAt: `${getDefaultDateISO()}T10:00:00.000Z`,
        months: 0,
        weeks: 0,
        days: 0,
        startTime: DEFAULT_START_TIME,
        endTime: DEFAULT_END_TIME,
        allDay: false,
        isFromAtsUser: false,
        atsUserId: null,
        isFromOwner: true,
        ownerType: ownerDefaultValue.value,
        priority: UserTaskPriority.None,
        primaryRecord: availableRecords[0],
        associatedRecords: [],
      }, true);
    } else {
      const isPrimaryStillValid = availableRecords.includes(formTask.primaryRecord);
      const nextPrimary = isPrimaryStillValid ? formTask.primaryRecord : availableRecords[0];
      const nextAssociated = isPrimaryStillValid
        ? (formTask.associatedRecords || []).filter(r => availableRecords.includes(r) && r !== nextPrimary)
        : [];
      // Backward compat: steps saved before this feature existed have no dateMode - they were always fixed-date.
      const nextDateMode = formTask.dateMode || UserTaskDateMode.Fixed;
      const patch = { ...formTask, primaryRecord: nextPrimary, associatedRecords: nextAssociated, dateMode: nextDateMode };

      if (nextDateMode === UserTaskDateMode.Relative) {
        patch.hours = null;
        if (patch.allDay) {
          patch.startTime = null;
          patch.endTime = null;
        } else {
          if (!patch.startTime) patch.startTime = DEFAULT_START_TIME;
          if (!patch.endTime) patch.endTime = DEFAULT_END_TIME;
        }
      }

      if (
        nextPrimary !== formTask.primaryRecord
        || nextAssociated.length !== (formTask.associatedRecords || []).length
        || nextDateMode !== formTask.dateMode
        || patch.startTime !== formTask.startTime
        || patch.endTime !== formTask.endTime
        || patch.hours !== formTask.hours
      ) {
        automationStore.setFormTask(patch, true);
      }
    }

    return function cleanup() {
      automationStore.resetFormTask();
    }
    // eslint-disable-next-line
  }, []);

  const startDate = formTask.startAt ? new Date(formTask.startAt) : null;
  const date = startDate && !isNaN(startDate.getTime()) ? formTask.startAt.slice(0, 10) : getDefaultDateISO();
  const time = startDate && !isNaN(startDate.getTime()) ? formTask.startAt.slice(11, 16) : '09:00';

  const endDate = formTask.endAt ? new Date(formTask.endAt) : null;
  // Legacy fixed-date tasks saved before endAt existed have no endAt - fall back to
  // start + 1h (mirroring the old auto-derivation) rather than a fixed 10:00, so an
  // afternoon start doesn't produce an end time earlier than the start.
  const endTime = endDate && !isNaN(endDate.getTime()) ? formTask.endAt.slice(11, 16) : addOneHour(time);

  const applyDateTime = (newDate, newStartTime, newEndTime, allDay) => {
    const data = { ...formTask, allDay };
    if (allDay) {
      data.startAt = `${newDate}T00:00:00.000Z`;
      data.endAt = `${newDate}T23:59:59.000Z`;
    } else {
      data.startAt = `${newDate}T${newStartTime}:00.000Z`;
      data.endAt = `${newDate}T${newEndTime}:00.000Z`;
    }
    automationStore.setFormTask(data);
  };

  const primaryRecord = formTask.primaryRecord || availableRecords[0];
  const associatedRecords = formTask.associatedRecords || [];

  const setPrimaryRecord = (record) => {
    automationStore.setFormTask({
      ...formTask,
      primaryRecord: record,
      // A record can't be both primary and associated at once
      associatedRecords: associatedRecords.filter(r => r !== record),
    });
  };

  const isRelativeDate = formTask.dateMode === UserTaskDateMode.Relative;

  const setDateMode = (mode) => {
    if (mode === formTask.dateMode) return;
    const data = { ...formTask, dateMode: mode };
    // Re-opening a step saved in the other mode leaves this mode's fields null (they're nulled
    // out on submit - see the roiData below). Backfill defaults on switch so hitting Save without
    // touching any other field still produces a valid date instead of a blank one.
    if (mode === UserTaskDateMode.Fixed && !data.startAt) {
      data.startAt = `${getDefaultDateISO()}T09:00:00.000Z`;
      data.endAt = `${getDefaultDateISO()}T10:00:00.000Z`;
      data.allDay = false;
    }
    if (mode === UserTaskDateMode.Relative) {
      data.months = data.months || 0;
      data.weeks = data.weeks || 0;
      data.days = data.days || 0;
      data.hours = null;
      if (data.allDay) {
        data.startTime = null;
        data.endTime = null;
      } else {
        data.startTime = data.startTime || DEFAULT_START_TIME;
        data.endTime = data.endTime || DEFAULT_END_TIME;
      }
    }
    automationStore.setFormTask(data);
  };

  // Same-day only: doesn't support an end time past midnight (e.g. start 23:00 / end 01:00).
  // Equal start/end is also rejected since a zero-duration task isn't meaningful.
  const isFixedTimeRangeInvalid = !isRelativeDate && !formTask.allDay
    && !!formTask.startAt && !!formTask.endAt
    && new Date(formTask.endAt) <= new Date(formTask.startAt);

  const isDisableButton = () => {
    if (!formTask.title || !primaryRecord) return true;
    if (!formTask.isFromAtsUser && !formTask.isFromOwner) return true;
    if (formTask.isFromAtsUser && !formTask.atsUserId) return true;
    if (isRelativeDate) {
      if (!((formTask.months || 0) + (formTask.weeks || 0) + (formTask.days || 0) > 0)) return true;
      if (!formTask.allDay && (!formTask.startTime || !formTask.endTime)) return true;
    } else if (isFixedTimeRangeInvalid) {
      return true;
    }
    return false;
  };

  return (
    <>
      <HeaderForm
        automationHeaderId="form-step-task"
        automationHeaderImgSrc={stepUpdateRecordIcon}
        automationHeaderImgAlt="icon-task"
        automationHeaderTextStep={RappidText.TaskStep}
      />

      {/* Task title */}
      <FormGroup className="step-body-text">
        <FL>Task title <span style={{ color: '#f7a8c4' }}>*</span></FL>
        <Input
          id="automation-task-title"
          className="automation-alert-input"
          disabled={!isDraft}
          value={formTask.title || ''}
          onChange={e => automationStore.setFormTask({ ...formTask, title: e.target.value })}
          style={{ borderRadius: 10 }}
        />
      </FormGroup>

      {/* Task date + All day */}
      <FormGroup className="step-body-text">
        <FL>Task date</FL>

        <SendFromButtonGroup style={{ marginBottom: 10 }}>
          <SendFromButton
            type="button"
            isSelected={!isRelativeDate}
            disabled={!isDraft}
            onClick={() => setDateMode(UserTaskDateMode.Fixed)}
          >
            Fixed date
          </SendFromButton>
          <SendFromButton
            type="button"
            isSelected={isRelativeDate}
            disabled={!isDraft}
            onClick={() => setDateMode(UserTaskDateMode.Relative)}
          >
            Relative to today
          </SendFromButton>
        </SendFromButtonGroup>

        {isRelativeDate ? (
          <RelativeDateInput formTask={formTask} isDisabled={!isDraft} />
        ) : (
          <FixedDateContainer>
            <DateTimeGrid $singleColumn>
              <input
                type="date"
                id="automation-task-date"
                className="automation-date-input"
                value={date}
                min={todayISO()}
                disabled={!isDraft}
                onChange={e => applyDateTime(e.target.value, time, endTime, formTask.allDay)}
              />
            </DateTimeGrid>

            {!formTask.allDay && (
              <DateTimeGrid style={{ paddingTop: 0 }}>
                <div>
                  <FL>Start time</FL>
                  <input
                    type="time"
                    id="automation-task-start-time"
                    className="automation-date-input"
                    value={time}
                    disabled={!isDraft}
                    onChange={e => applyDateTime(date, e.target.value, endTime, formTask.allDay)}
                  />
                </div>
                <div>
                  <FL>End time</FL>
                  <input
                    type="time"
                    id="automation-task-end-time"
                    className="automation-date-input"
                    value={endTime}
                    disabled={!isDraft}
                    onChange={e => applyDateTime(date, time, e.target.value, formTask.allDay)}
                  />
                </div>
              </DateTimeGrid>
            )}

            {isFixedTimeRangeInvalid && (
              <p style={{ fontSize: 11.5, color: '#f7a8c4', margin: '0 12px 10px' }}>
                End time must be after start time.
              </p>
            )}

            <AllDayRow>
              <AllDayToggleLabel>All day</AllDayToggleLabel>
              <Switch
                checked={!!formTask.allDay}
                onChange={checked => applyDateTime(date, time, endTime, checked)}
                disabled={!isDraft}
                onColor="#ef4277"
                height={20}
                width={36}
                handleDiameter={14}
                uncheckedIcon={false}
                checkedIcon={false}
              />
            </AllDayRow>
          </FixedDateContainer>
        )}
      </FormGroup>

      {/* Priority */}
      <FormGroup className="step-body-text">
        <FL>Priority</FL>
        <Select
          id="automation-task-priority"
          classNamePrefix="automation-dropdown"
          isDisabled={!isDraft}
          options={PRIORITY_OPTIONS}
          value={PRIORITY_OPTIONS.find(x => x.value === formTask.priority) || PRIORITY_OPTIONS[0]}
          onChange={e => automationStore.setFormTask({ ...formTask, priority: e.value })}

          isSearchable={false}
        />
      </FormGroup>

      {/* Task assignee */}
      <FormGroup className="step-body-text">
        <FL>Task assignee</FL>
        <SendFromButtonGroup>
          <SendFromButton
            type="button"
            isSelected={!!formTask.isFromAtsUser}
            disabled={!isDraft}
            onClick={() => {
              if (!isDraft) return;
              automationStore.setFormTask({
                ...formTask,
                isFromAtsUser: !formTask.isFromAtsUser,
                atsUserId: formTask.isFromAtsUser ? null : formTask.atsUserId,
                isFromOwner: false,
                ownerType: null,
              });
            }}
          >
            Database user
          </SendFromButton>
          <SendFromButton
            type="button"
            isSelected={!!formTask.isFromOwner && !formTask.isFromAtsUser}
            disabled={!isDraft}
            onClick={() => {
              if (!isDraft) return;
              const newIsFromOwner = !formTask.isFromOwner;
              automationStore.setFormTask({
                ...formTask,
                isFromOwner: newIsFromOwner,
                ownerType: newIsFromOwner ? ownerDefaultValue.value : null,
                isFromAtsUser: false,
                atsUserId: null,
              });
            }}
          >
            Recruiter
          </SendFromButton>
        </SendFromButtonGroup>

        {formTask.isFromAtsUser && (
          <SendFromDropdownContainer>
            <Select
              isDisabled={!isDraft}
              id="automation-task-assignee-atsuser"
              classNamePrefix="automation-dropdown"
              placeholder="Select Database User"
              options={automationUsers}
              getOptionLabel={option => option.label}
              getOptionValue={option => option.id}
              value={formTask.atsUserId ? (automationUsers?.find(x => x.id === formTask.atsUserId) ?? null) : null}
              onChange={e => automationStore.setFormTask({ ...formTask, atsUserId: e ? e.id : null })}
              isSearchable={false}
              noOptionsMessage={() => "No ATS users found"}
            />
            {automationUsers?.length === 0 && (
              <Hint>No ATS users are synced for this company yet. Connect your ATS to assign a specific user.</Hint>
            )}
          </SendFromDropdownContainer>
        )}

        {formTask.isFromOwner && !formTask.isFromAtsUser && isPlacementOrJobBased && (
          <SendFromDropdownContainer>
            <Select
              isDisabled={!isDraft}
              id="automation-task-ownertype-select"
              classNamePrefix="automation-dropdown"
              placeholder="Select recruiter type"
              options={getFilteredOwnerOptions()}
              getOptionLabel={option => option.label}
              getOptionValue={option => option.value}
              value={formTask.ownerType ? (ownerTypeOptions.find(x => x.value === formTask.ownerType) ?? ownerDefaultValue) : ownerDefaultValue}
              onChange={e => automationStore.setFormTask({ ...formTask, ownerType: e.value })}
              isSearchable={false}
            />
          </SendFromDropdownContainer>
        )}
      </FormGroup>

      {/* Primary record */}
      <FormGroup className="step-body-text">
        <FL>Primary record</FL>
        <Select
          id="automation-task-primary-record"
          classNamePrefix="automation-dropdown"
          isDisabled={!isDraft}
          options={availableRecords.map(r => ({ value: r, label: RECORD_TYPE_LABEL[r] }))}
          value={{ value: primaryRecord, label: RECORD_TYPE_LABEL[primaryRecord] }}
          onChange={e => setPrimaryRecord(e.value)}
          isSearchable={false}
        />
        <Hint>The task is created against whichever record you pick here.</Hint>
      </FormGroup>

      {/* Associated records */}
      {availableRecords.length > 1 ? (
        <FormGroup className="step-body-text">
          <FL>
            Associated records{' '}
            <span style={{ color: '#9a9a9c', fontSize: 11.5 }}>(optional)</span>
          </FL>
          <StyledMultiSelectWithSearch
            showSelectAll
            showTags
            valueKey="value"
            labelKey="label"
            className="automation-form-dropdown-multi"
            id="automation-task-associated-records"
            name="automation-task-associated-records"
            placeholder="- Select -"
            isDisabled={!isDraft}
            options={availableRecords.filter(r => r !== primaryRecord).map(r => ({ value: r, label: RECORD_TYPE_LABEL[r] }))}
            value={associatedRecords.map(r => ({ value: r, label: RECORD_TYPE_LABEL[r] }))}
            onChange={selected => automationStore.setFormTask({
              ...formTask,
              associatedRecords: (selected || []).map(s => s.value),
            })}
          />
          <Hint>Optionally also link this task to the other records in context for this campaign.</Hint>
        </FormGroup>
      ) : (
        <p className="step-body-text" style={{ fontSize: 12, color: '#9a9a9c' }}>
          No associated records for this campaign type.
        </p>
      )}

      {isDraft &&
        <SubmitButton
          type={RappidNodeType.Task}
          text={setTaskNodeTextLine1(formTask)}
          text2={setTaskNodeTextLine2(formTask)}
          roiData={(() => {
            const { hours, ...formTaskPayload } = formTask;
            return isRelativeDate
              ? {
                  ...formTaskPayload,
                  startAt: null,
                  endAt: null,
                  // Sent as "HH:mm:ss" TimeSpan strings - the backend's TimeSpan JSON converter
                  // requires the full "c" format and rejects a bare "HH:mm".
                  startTime: formTask.allDay ? null : toTimeSpan(toTimeInputValue(formTask.startTime, DEFAULT_START_TIME.slice(0, 5))),
                  endTime: formTask.allDay ? null : toTimeSpan(toTimeInputValue(formTask.endTime, DEFAULT_END_TIME.slice(0, 5))),
                }
              : {
                  ...formTaskPayload,
                  months: null,
                  weeks: null,
                  days: null,
                  startTime: formTask.allDay ? null : toTimeSpan(time),
                  endTime: formTask.allDay ? null : toTimeSpan(endTime),
                };
          })()}
          validateConditions={() => validateNodeAndResetBorder()}
          isDisabled={isDisableButton()}
        />
      }
    </>
  );
});

export default TaskStepForm;
